import React, { useEffect, useState, useCallback } from 'react';
import { getReviewSummary } from '../api';
import { RefreshCw, BarChart3, Target, Clock, Layers, Award, LogOut, TrendingUp } from 'lucide-react';

type SampleKey = 'all' | 'last_30' | 'last_7d' | 'last_30d';
type SourceKey = 'all' | 'paper' | 'tradovate';

const SAMPLE_OPTIONS: { value: SampleKey; label: string }[] = [
  { value: 'all', label: 'All Trades' },
  { value: 'last_30', label: 'Last 30 Trades' },
  { value: 'last_7d', label: 'Last 7 Days' },
  { value: 'last_30d', label: 'Last 30 Days' },
];

const SOURCE_OPTIONS: { value: SourceKey; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'paper', label: 'Paper Only' },
  { value: 'tradovate', label: 'Tradovate Only' },
];

const SIGNAL_CLASS_ORDER = ['SMR', 'MACRO_LIQ', 'MACRO_ONLY', 'BREAKER_ONLY', 'TREND_CONT', 'SECOND_CHANCE'];
const ENTRY_MODE_ORDER = ['confirmation', 'retest_any', 'retest_deep'];
const SMT_ORDER = ['dual', 'es_only', 'ym_only', 'none'];
const QUALITY_ORDER = ['<30', '30-49', '50-69', '70-84', '85+'];

const fmtPct = (n: number | undefined | null) => `${(n ?? 0).toFixed(1)}%`;
const fmtR = (n: number | undefined | null) => `${(n ?? 0).toFixed(2)}R`;
const fmtMoney = (n: number | undefined | null) => {
  const v = n ?? 0;
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

type Bucket = {
  count: number;
  win_rate: number;
  avg_r: number;
  total_pnl: number;
  avg_pnl: number;
  wins: number;
  losses: number;
};

type ExitBucket = { count: number; total_pnl?: number; avg_pnl?: number; total_loss?: number; avg_loss?: number };

type Summary = {
  sample: string;
  source: string;
  generated_at: string;
  overall: Bucket;
  by_signal_class: Record<string, Bucket>;
  by_entry_mode: Record<string, Bucket>;
  by_macro_window: Record<string, Bucket>;
  by_smt_strength_15m: Record<string, Bucket>;
  by_smt_strength_1m: Record<string, Bucket>;
  loss_distribution: Record<string, ExitBucket>;
  exit_distribution: Record<string, ExitBucket>;
  by_quality_score: Record<string, Bucket>;
};

export default function Review() {
  const [sample, setSample] = useState<SampleKey>('all');
  const [source, setSource] = useState<SourceKey>('all');
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getReviewSummary(sample, source);
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [sample, source]);

  useEffect(() => {
    load();
  }, [load]);

  const count = data?.overall?.count ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-xl font-bold">Review</h1><p className="text-xs text-gray-500 mt-0.5">Performance analysis. Win rate, profit factor, drawdown, and expectancy broken down by class, mode, and time period.</p></div>
        <div className="flex items-center gap-2">
          <select
            value={sample}
            onChange={(e) => setSample(e.target.value as SampleKey)}
            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-xs"
          >
            {SAMPLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as SourceKey)}
            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-xs"
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 border border-gray-700 rounded px-3 py-1.5 text-xs font-medium"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>
      ) : count === 0 ? (
        <div className="bg-gray-900 rounded-lg border border-gray-800 p-12 flex flex-col items-center justify-center text-center">
          <BarChart3 size={36} className="text-gray-700 mb-3" />
          <p className="text-gray-400 text-sm">No closed trades yet. Run the system for a few sessions, then come back.</p>
        </div>
      ) : data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <OverallCard overall={data.overall} />
          <ClassCard
            title="By Signal Class"
            icon={Layers}
            order={SIGNAL_CLASS_ORDER}
            data={data.by_signal_class}
            labelHeader="Class"
          />
          <ClassCard
            title="By Entry Mode"
            icon={Target}
            order={ENTRY_MODE_ORDER}
            data={data.by_entry_mode}
            labelHeader="Mode"
          />
          <MacroWindowCard data={data.by_macro_window} />
          <QualityCard data={data.by_quality_score} />
          <SmtCard
            title="SMT Correlation — 15m"
            subtitle="Does dual SMT outperform single? Compare to 1m below."
            data={data.by_smt_strength_15m}
          />
          <SmtCard
            title="SMT Correlation — 1m"
            subtitle="Compare to 15m above to gauge fast-timeframe divergence signal."
            data={data.by_smt_strength_1m}
          />
          <ExitDistributionCard exits={data.exit_distribution} losses={data.loss_distribution} />
        </div>
      ) : null}
    </div>
  );
}

function CardShell({ title, icon: Icon, subtitle, span, children }: any) {
  return (
    <div
      className={`bg-gray-900 rounded-lg border border-gray-800 p-4 hover:border-gray-700 transition-colors ${span === 'full' ? 'lg:col-span-2' : ''}`}
    >
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-gray-500" />}
        <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">{title}</h3>
      </div>
      {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
      <div className={subtitle ? '' : 'mt-3'}>{children}</div>
    </div>
  );
}

function OverallCard({ overall }: { overall: Bucket }) {
  const pnlColor = overall.total_pnl >= 0 ? 'text-green-400' : 'text-red-400';
  const avgPnlColor = overall.avg_pnl >= 0 ? 'text-green-400' : 'text-red-400';
  const rColor = overall.avg_r >= 0 ? 'text-green-400' : 'text-red-400';
  return (
    <CardShell title="Overall Performance" icon={TrendingUp} span="full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-3xl font-bold font-mono text-white">{fmtPct(overall.win_rate)}</div>
          <div className="text-xs text-gray-500 mt-1">win rate</div>
          <div className="text-xs text-gray-400 mt-2">
            <span className="text-green-400 font-bold">{overall.wins}W</span>
            <span className="text-gray-600 mx-1">/</span>
            <span className="text-red-400 font-bold">{overall.losses}L</span>
            <span className="text-gray-500"> · {overall.count} trades</span>
          </div>
        </div>
        <div>
          <div className={`text-3xl font-bold font-mono ${rColor}`}>{fmtR(overall.avg_r)}</div>
          <div className="text-xs text-gray-500 mt-1">average</div>
          <div className="text-xs text-gray-400 mt-2">
            <span className={`font-mono font-bold ${pnlColor}`}>{fmtMoney(overall.total_pnl)}</span>
            <span className="text-gray-500"> total P&L · </span>
            <span className={`font-mono ${avgPnlColor}`}>{fmtMoney(overall.avg_pnl)}</span>
            <span className="text-gray-500"> avg/trade</span>
          </div>
        </div>
      </div>
    </CardShell>
  );
}

function winRateBarColor(wr: number, bestWr: number) {
  if (wr === bestWr && wr > 0) return 'bg-green-900/30';
  if (wr > 0 && wr < 40) return 'bg-red-900/30';
  return '';
}

function ClassCard({
  title,
  icon,
  order,
  data,
  labelHeader,
}: {
  title: string;
  icon: any;
  order: string[];
  data: Record<string, Bucket>;
  labelHeader: string;
}) {
  const rows = order
    .map((key) => ({ key, bucket: data[key] }))
    .filter((r) => r.bucket && r.bucket.count > 0);
  const bestWr = rows.reduce((max, r) => Math.max(max, r.bucket.win_rate), 0);
  const maxCount = rows.reduce((max, r) => Math.max(max, r.bucket.count), 0);

  return (
    <CardShell title={title} icon={icon}>
      {rows.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center">No data for this slice</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-2">{labelHeader}</th>
                <th className="text-right p-2">Count</th>
                <th className="text-right p-2">Win Rate</th>
                <th className="text-right p-2">Avg R</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, bucket }) => {
                const bg = winRateBarColor(bucket.win_rate, bestWr);
                const barPct = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
                return (
                  <tr
                    key={key}
                    className={`border-b border-gray-800 hover:bg-gray-800/50 ${bg}`}
                    title={`${bucket.wins}W / ${bucket.losses}L · ${fmtMoney(bucket.total_pnl)} total · ${fmtMoney(bucket.avg_pnl)} avg`}
                  >
                    <td className="p-2 font-medium text-gray-200">{key}</td>
                    <td className="p-2 text-right font-mono relative">
                      <div className="relative">
                        <div
                          className="absolute inset-y-0 right-0 bg-blue-500/10 rounded"
                          style={{ width: `${barPct}%` }}
                        />
                        <span className="relative">{bucket.count}</span>
                      </div>
                    </td>
                    <td
                      className={`p-2 text-right font-mono font-bold ${
                        bucket.win_rate === bestWr && bucket.win_rate > 0
                          ? 'text-green-400'
                          : bucket.win_rate < 40
                          ? 'text-red-400'
                          : 'text-gray-300'
                      }`}
                    >
                      {fmtPct(bucket.win_rate)}
                    </td>
                    <td
                      className={`p-2 text-right font-mono ${
                        bucket.avg_r >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {fmtR(bucket.avg_r)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CardShell>
  );
}

function MacroWindowCard({ data }: { data: Record<string, Bucket> }) {
  const rows = Object.entries(data)
    .filter(([, b]) => b && b.count > 0)
    .map(([key, bucket]) => ({ key, bucket }))
    .sort((a, b) => b.bucket.total_pnl - a.bucket.total_pnl);

  const top3 = new Set(rows.slice(0, 3).map((r) => r.key));
  const bottom3 = new Set(rows.slice(-3).map((r) => r.key));

  return (
    <CardShell title="By Macro Window" icon={Clock}>
      {rows.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center">No data for this slice</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-2">Window</th>
                <th className="text-right p-2">Count</th>
                <th className="text-right p-2">Win Rate</th>
                <th className="text-right p-2">Avg R</th>
                <th className="text-right p-2">Total P&L</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, bucket }) => {
                const isTop = top3.has(key);
                const isBottom = !isTop && bottom3.has(key);
                const bg = isTop ? 'bg-green-900/20' : isBottom ? 'bg-red-900/20' : '';
                return (
                  <tr
                    key={key}
                    className={`border-b border-gray-800 hover:bg-gray-800/50 ${bg}`}
                    title={`${bucket.wins}W / ${bucket.losses}L · ${fmtMoney(bucket.avg_pnl)} avg/trade`}
                  >
                    <td className="p-2 font-mono text-gray-200">{key}</td>
                    <td className="p-2 text-right font-mono">{bucket.count}</td>
                    <td className="p-2 text-right font-mono">{fmtPct(bucket.win_rate)}</td>
                    <td
                      className={`p-2 text-right font-mono ${
                        bucket.avg_r >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {fmtR(bucket.avg_r)}
                    </td>
                    <td
                      className={`p-2 text-right font-mono font-bold ${
                        bucket.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {fmtMoney(bucket.total_pnl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CardShell>
  );
}

function SmtCard({
  title,
  subtitle,
  data,
}: {
  title: string;
  subtitle: string;
  data: Record<string, Bucket>;
}) {
  const rows = SMT_ORDER.map((key) => ({ key, bucket: data[key] })).filter(
    (r) => r.bucket && r.bucket.count > 0
  );

  return (
    <CardShell title={title} icon={Award} subtitle={subtitle}>
      {rows.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center">No data for this slice</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-2">SMT</th>
                <th className="text-right p-2">Count</th>
                <th className="text-right p-2">Win Rate</th>
                <th className="text-right p-2">Avg R</th>
                <th className="text-right p-2">Total P&L</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, bucket }) => (
                <tr
                  key={key}
                  className="border-b border-gray-800 hover:bg-gray-800/50"
                  title={`${bucket.wins}W / ${bucket.losses}L · ${fmtMoney(bucket.avg_pnl)} avg`}
                >
                  <td className="p-2 font-medium text-gray-200">{key}</td>
                  <td className="p-2 text-right font-mono">{bucket.count}</td>
                  <td className="p-2 text-right font-mono">{fmtPct(bucket.win_rate)}</td>
                  <td
                    className={`p-2 text-right font-mono ${
                      bucket.avg_r >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {fmtR(bucket.avg_r)}
                  </td>
                  <td
                    className={`p-2 text-right font-mono font-bold ${
                      bucket.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {fmtMoney(bucket.total_pnl)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardShell>
  );
}

function QualityCard({ data }: { data: Record<string, Bucket> }) {
  const rows = QUALITY_ORDER.map((key) => ({ key, bucket: data[key] })).filter(
    (r) => r.bucket && r.bucket.count > 0
  );

  return (
    <CardShell
      title="By Quality Score Bucket"
      icon={Award}
      subtitle="Verifies the 50+ auto-execute threshold is well-placed."
    >
      {rows.length === 0 ? (
        <div className="text-xs text-gray-600 py-4 text-center">No data for this slice</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                <th className="text-left p-2">Bucket</th>
                <th className="text-right p-2">Count</th>
                <th className="text-right p-2">Win Rate</th>
                <th className="text-right p-2">Avg R</th>
                <th className="text-right p-2">Total P&L</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, bucket }) => {
                const autoThreshold = key === '50-69' || key === '70-84' || key === '85+';
                return (
                  <tr
                    key={key}
                    className={`border-b border-gray-800 hover:bg-gray-800/50 ${
                      autoThreshold ? 'bg-blue-900/10' : ''
                    }`}
                    title={`${bucket.wins}W / ${bucket.losses}L · ${fmtMoney(bucket.avg_pnl)} avg`}
                  >
                    <td className="p-2 font-mono text-gray-200">
                      {key}
                      {autoThreshold && (
                        <span className="ml-2 text-[10px] text-blue-400 uppercase">auto</span>
                      )}
                    </td>
                    <td className="p-2 text-right font-mono">{bucket.count}</td>
                    <td className="p-2 text-right font-mono">{fmtPct(bucket.win_rate)}</td>
                    <td
                      className={`p-2 text-right font-mono ${
                        bucket.avg_r >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {fmtR(bucket.avg_r)}
                    </td>
                    <td
                      className={`p-2 text-right font-mono font-bold ${
                        bucket.total_pnl >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {fmtMoney(bucket.total_pnl)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CardShell>
  );
}

function ExitDistributionCard({
  exits,
  losses,
}: {
  exits: Record<string, ExitBucket>;
  losses: Record<string, ExitBucket>;
}) {
  const winners = Object.entries(exits)
    .filter(([, b]) => (b?.total_pnl ?? 0) > 0)
    .sort((a, b) => (b[1].total_pnl ?? 0) - (a[1].total_pnl ?? 0));
  const losersList = Object.entries(losses)
    .filter(([, b]) => b && b.count > 0)
    .sort((a, b) => (a[1].total_loss ?? 0) - (b[1].total_loss ?? 0));

  return (
    <CardShell title="Exit Distribution" icon={LogOut} span="full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs text-green-400 font-semibold uppercase tracking-wide mb-2">
            Winners
          </h4>
          {winners.length === 0 ? (
            <div className="text-xs text-gray-600 py-4 text-center">No winning exits</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-2">Exit</th>
                  <th className="text-right p-2">Count</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-right p-2">Avg</th>
                </tr>
              </thead>
              <tbody>
                {winners.map(([key, b]) => (
                  <tr key={key} className="border-b border-gray-800 hover:bg-gray-800/50">
                    <td className="p-2 font-mono text-gray-200">{key}</td>
                    <td className="p-2 text-right font-mono">{b.count}</td>
                    <td className="p-2 text-right font-mono text-green-400 font-bold">
                      {fmtMoney(b.total_pnl)}
                    </td>
                    <td className="p-2 text-right font-mono text-green-400">
                      {fmtMoney(b.avg_pnl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <h4 className="text-xs text-red-400 font-semibold uppercase tracking-wide mb-2">Losers</h4>
          {losersList.length === 0 ? (
            <div className="text-xs text-gray-600 py-4 text-center">No losing exits</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-xs uppercase border-b border-gray-800">
                  <th className="text-left p-2">Exit</th>
                  <th className="text-right p-2">Count</th>
                  <th className="text-right p-2">Total</th>
                  <th className="text-right p-2">Avg</th>
                </tr>
              </thead>
              <tbody>
                {losersList.map(([key, b]) => (
                  <tr key={key} className="border-b border-gray-800 hover:bg-gray-800/50">
                    <td className="p-2 font-mono text-gray-200">{key}</td>
                    <td className="p-2 text-right font-mono">{b.count}</td>
                    <td className="p-2 text-right font-mono text-red-400 font-bold">
                      {fmtMoney(b.total_loss)}
                    </td>
                    <td className="p-2 text-right font-mono text-red-400">
                      {fmtMoney(b.avg_loss)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </CardShell>
  );
}
